import {FC} from 'react'
import {Link} from 'react-router'

import {KTIcon} from '_metronic/helpers'
import {useT} from '_metronic/i18n/t'

import type {PortalAccessStatus} from './accessStatus'

type Props = {
  status: PortalAccessStatus
  /** The fee section path, when the viewer may open it (fee_manager); null hides the button. */
  feePath: string | null
}

/**
 * The portal lock screen — PHP `site/defaulteraccessdisable`: an alert with
 * the school's message, "Logout", and "Fee Voucher" for a fee-defaulter lock
 * (not for a restricted login). A parent can still switch child from the
 * header.
 */
const AccessDisabledPage: FC<Props> = ({status, feePath}) => {
  const t = useT()
  const isFee = status.reason === 'fee_defaulter'
  const message =
    status.message?.trim() ||
    t(isFee ? 'SP_HOME.ACCESS.DEFAULT_FEE_MESSAGE' : 'SP_HOME.ACCESS.DEFAULT_RESTRICTED_MESSAGE')

  return (
    <div className='d-flex justify-content-center py-10' data-testid='sp-access-disabled'>
      <div
        className='card shadow-sm mw-600px w-100'
        role='alertdialog'
        aria-labelledby='sp-access-title'
      >
        <div className='card-header border-0 pt-6'>
          <h3 className='card-title fw-bolder d-flex align-items-center gap-2' id='sp-access-title'>
            <KTIcon iconName='information-5' className='fs-1 text-danger' />
            {t('SP_HOME.ACCESS.TITLE')}
          </h3>
        </div>
        <div className='card-body pt-2'>
          <div className='alert alert-danger mb-0' data-testid='sp-access-message'>
            <div className='fs-5 fw-semibold text-gray-900' style={{whiteSpace: 'pre-line'}}>
              {message}
            </div>
          </div>
        </div>
        <div className='card-footer d-flex justify-content-end gap-3'>
          <Link to='/logout' className='btn btn-light' data-testid='sp-access-logout'>
            {t('MENU.SIGN_OUT')}
          </Link>
          {isFee && feePath && (
            <Link to={feePath} className='btn btn-success' data-testid='sp-access-fee'>
              {t('FEE.VOUCHER')}
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default AccessDisabledPage
